/* eslint-disable camelcase */
import { links } from "%/enums/link-enums";
import { useGeneralStore } from "%/stores/general-store";
import {
  EMAIL_FORM,
  EXPERT_SIGN_UP_FORM,
  INVITED_PASSWORD_RESET_FORM,
  PASSWORD_FORM,
  TWO_FACTOR_ENROLL_FORM,
  TWO_FACTOR_FORM,
  ACCOUNT_DELETED_ERROR_CODE,
} from "@/constants/login-flow-constants";
import { resolveEmailCheckOutcome } from "%/dynamic-component-editor/helpers/login-flow-steps";
import { createPasskey, getPasskeyAssertion } from "%/utils/webauthn";
import { ToastBuilder, validateEmail } from "@vcl";
import { $axios } from "@bloglovin/vue-framework";
import { defineStore } from "pinia";
import SmartFeedEngine from "SmartFeed/smart-feed-engine";

export const useUserStore = defineStore("user-store", {
  state: () => ({
    userAuthEmail: "",
    userFlowSteps: [EMAIL_FORM],
    expertFlowSteps: [EXPERT_SIGN_UP_FORM],
    userFirstName: "",
    userLastName: "",
    emailStatus: {
      requestedNewEmail: "",
      isEmailConfirmed: true,
      isEmailBeingChanged: false,
    },
    isLoading: false,
    loadingChangeFirstName: false,
    loadingChangeLastName: false,
    loginErrorMessage: "",
    signupErrorMessage: "",
    forgotPasswordErrorMessage: "",
    resetPasswordErrorMessage: "",
    verifyEmailErrorMessage: "",
    logoutErrorMessage: "",
    userDetailsErrorMessage: "",
    verifyUserErrorMessage: "",
    signUpNote: "",
    resetPasswordError: "",
    invitingOrgId: 0,
    invitationToken: "",
    invitedUserId: 0,
    invitedPublicationId: 0,
    invitedPublicationName: "",
    changeEmailError: "",
    isModerator: false,
    redirect: "",
    loginWithInspirationRequestId: 0,
    loginWithInspirationRequestToken: "",
    successfulPasswordChange: false,
    isDeletingAccount: false,
    deleteAccountErrorMessage: "",
    mfaChallengeToken: "",
    mfaMethods: [],
    passkeyAssertionOptions: null,
    twoFactorErrorMessage: "",
    forcedEnrollSecret: "",
    forcedEnrollOtpauthUri: "",
    forcedEnrollQrDataUri: "",
    forcedEnrollBackupCodes: [],
  }),
  getters: {
    appId() {
      return useGeneralStore().appId;
    },
    hasValidUserAuthEmail() {
      return validateEmail(this.userAuthEmail);
    },
    currentUserFlowStep() {
      return this.userFlowSteps.at(-1);
    },
    previousUserFlowStep() {
      return this.userFlowSteps.at(-2);
    },
    currentExpertFlowStep() {
      return this.expertFlowSteps.at(-1);
    },
    usersApiV1Url() {
      return useGeneralStore().getLink(links.usersApiV1);
    },
    emailData() {
      return this.userData.email_data ?? {};
    },
    userId() {
      return useGeneralStore().userId;
    },
    userData() {
      return useGeneralStore().userData;
    },
  },
  actions: {
    uploadAvatar(avatarFile) {
      let formData = new FormData();
      formData.append("avatar", avatarFile);
      $axios
          .post("/user/avatar/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            if (response.data.success) {
              useGeneralStore().setUserData(response.data.userData);
            }
          });
    },
    loginUser(password) {
      this.isLoading = true;
      $axios
          .post("/user/login", {
            app_id: this.appId,
            email: this.userAuthEmail,
            password: password,
            login_with_inspiration_request_id: this.loginWithInspirationRequestId,
            login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
            redirect: this.redirect,
          })
          .then(response => {
            if (response.data.result === "mfa_required") {
              this.mfaChallengeToken = response.data.challenge_token;
              this.mfaMethods = response.data.methods ?? [];
              this.passkeyAssertionOptions = response.data.passkey_assertion_options ?? null;
              this.twoFactorErrorMessage = "";
              this.userFlowSteps.push(TWO_FACTOR_FORM);
              return;
            }
            if (response.data.result === "mfa_enrollment_required") {
              this.mfaChallengeToken = response.data.challenge_token;
              this.twoFactorErrorMessage = "";
              this.userFlowSteps.push(TWO_FACTOR_ENROLL_FORM);
              return;
            }
            if (response.data.success) {
              window.location.href = this.redirect;
              return;
            }
            if (response.data.old_password) {
              this.loginErrorMessage = `You entered an old password, you changed it ${response.data.date_changed}`;
            } else {
              this.loginErrorMessage = "login failed for an unknown reason";
            }
          })
          .catch(error => {
            this.loginErrorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    completeSecondFactor(endpoint, payload) {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      return $axios
          .post(endpoint, {
            challenge_token: this.mfaChallengeToken,
            login_with_inspiration_request_id: this.loginWithInspirationRequestId,
            login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
            redirect: this.redirect,
            ...payload,
          })
          .then(response => {
            if (response.data.success) {
              window.location.href = this.redirect;
              return;
            }
            this.twoFactorErrorMessage = "Verification failed, please try again.";
          })
          .catch(error => {
            this.twoFactorErrorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    verifyCode(code) {
      return this.completeSecondFactor("/user/login/verify-code", { code });
    },

    async verifyPasskey() {
      if (!this.passkeyAssertionOptions) {
        this.twoFactorErrorMessage = "No passkey is registered for this account.";
        return;
      }
      this.twoFactorErrorMessage = "";
      try {
        const assertion = await getPasskeyAssertion(this.passkeyAssertionOptions);
        return this.completeSecondFactor("/user/login/verify-passkey", assertion);
      } catch (error) {
        this.twoFactorErrorMessage = error?.message ?? "Passkey authentication was cancelled.";
      }
    },

    enrollForcedTotp() {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      return $axios
          .post("/user/login/enroll-totp", { challenge_token: this.mfaChallengeToken })
          .then(response => {
            this.forcedEnrollSecret = response.data.secret;
            this.forcedEnrollOtpauthUri = response.data.otpauth_uri;
            this.forcedEnrollQrDataUri = response.data.qr_svg_data_uri;
          })
          .catch(error => {
            this.twoFactorErrorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    confirmForcedTotp(code) {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      return $axios
          .post("/user/login/confirm-totp", {
            challenge_token: this.mfaChallengeToken,
            login_with_inspiration_request_id: this.loginWithInspirationRequestId,
            login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
            redirect: this.redirect,
            code,
          })
          .then(response => {
            if (response.data.success) {
              this.forcedEnrollBackupCodes = response.data.backup_codes ?? [];
              if (this.forcedEnrollBackupCodes.length === 0) {
                window.location.href = this.redirect;
              }
              return;
            }
            this.twoFactorErrorMessage = "Verification failed, please try again.";
          })
          .catch(error => {
            this.twoFactorErrorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    async forcedRegisterPasskey(alias) {
      this.isLoading = true;
      this.twoFactorErrorMessage = "";
      try {
        const optionsResponse = await $axios.post("/user/login/passkey/register-options", {
          challenge_token: this.mfaChallengeToken,
        });
        const attestation = await createPasskey(optionsResponse.data.public_key);
        const response = await $axios.post("/user/login/passkey/register", {
          challenge_token: this.mfaChallengeToken,
          registration_token: optionsResponse.data.registration_token,
          alias,
          login_with_inspiration_request_id: this.loginWithInspirationRequestId,
          login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
          redirect: this.redirect,
          ...attestation,
        });
        if (response.data.success) {
          this.forcedEnrollBackupCodes = response.data.backup_codes ?? [];
          if (this.forcedEnrollBackupCodes.length === 0) {
            window.location.href = this.redirect;
          }
          return;
        }
        this.twoFactorErrorMessage = "Passkey registration failed, please try again.";
      } catch (error) {
        this.twoFactorErrorMessage = error?.response ? this.parseError(error) : error?.message ?? "Could not register a passkey.";
      } finally {
        this.isLoading = false;
      }
    },

    finishForcedEnroll() {
      window.location.href = this.redirect;
    },

    parseError(error) {
      const errorData = error?.response?.data ?? {};
      if (errorData.code === ACCOUNT_DELETED_ERROR_CODE && errorData.message) {
        return errorData.message;
      }
      if (errorData.message === "The password you entered is incorrect. Please try again.") {
        return "Incorrect password, please try again.";
      }
      return "Login failed, please try again.";
    },
    authenticateLoginWithInspiration() {
      this.isLoading = true;
      $axios
          .post("/user/login-with-inspiration/authenticate", {
            login_with_inspiration_request_id: this.loginWithInspirationRequestId,
            login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
            redirect: this.redirect,
          })
          .then(response => {
            if (response.data.redirect) {
              window.location.href = response.data.redirect;
            } else {
              window.location.href = this.redirect;
            }
          })
          .catch(error => {
            this.signupErrorMessage = this.parseError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    validateSignupFormData(firstName, lastName, email, password, confirmPassword) {
      if (!firstName.trim()) {
        this.signupErrorMessage = "First name is required";
        return false;
      }
      if (!lastName.trim()) {
        this.signupErrorMessage = "Last name is required";
        return false;
      }
      if (!email.trim()) {
        this.signupErrorMessage = "Email is required";
        return false;
      }
      if (!validateEmail(email)) {
        this.signupErrorMessage = "Please enter a valid email address";
        return false;
      }
      if (!password.trim()) {
        this.signupErrorMessage = "Password is required";
        return false;
      }
      if (!confirmPassword.trim()) {
        this.signupErrorMessage = "Confirm password is required";
        return false;
      }
      if (password !== confirmPassword) {
        this.signupErrorMessage = "Passwords do not match";
        return false;
      }

      this.signupErrorMessage = "";
      return true;
    },
    signUpUser(firstName, lastName, orgName, email, password, signupIntent = "creator") {
      orgName = orgName || `${firstName} ${lastName}`;

      this.isLoading = true;
      $axios
          .post("/user/create", {
            org_name: orgName,
            first_name: firstName,
            last_name: lastName,
            email: email,
            password: password,
            app_id: this.appId,
            invitation_token: this.invitationToken,
            inviting_org_id: this.invitingOrgId,
            login_with_inspiration_request_id: this.loginWithInspirationRequestId,
            login_with_inspiration_request_token: this.loginWithInspirationRequestToken,
            redirect: this.redirect,
            signup_intent: signupIntent,
          })
          .then(response => {
            const orgId = response.data.orgData.org_id;
            if (signupIntent === "expert") {
              window.location.href = `/${orgId}/creator`;
            } else if (response.data.redirect) {
              window.location.href = response.data.redirect;
            } else {
              window.location.href = this.redirect;
            }
          })
          .catch(error => {
            if (error.message) {
              this.signupErrorMessage = error.message;
            } else {
              this.signupErrorMessage =
              "something unexpected went wrong during signup";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    signUpAndAcceptInvitation(password) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        "/user/invitation/publication/accept",
        {
          user_id: this.invitedUserId,
          first_name: this.userFirstName,
          last_name: this.userLastName,
          password: password,
          token: this.invitationToken,
          publication_id: this.invitedPublicationId,
        },
        () => {
          window.location.href = this.redirect;
        },
        error => {
          this.signupErrorMessage =
            error?.response?.data?.message ||
            "something unexpected went wrong during signup";
        },
        () => {
          this.isLoading = false;
        },
      );
    },
    acceptInvitation(userId, token, firstName, lastName, password, orgId) {
      this.isLoading = true;
      $axios
          .post("/user/invitation/accept", {
            user_id: userId,
            first_name: firstName,
            last_name: lastName,
            password: password,
            app_id: this.appId,
            org_id: orgId,
            token: token,
          })
          .then(() => {
            window.location.href = "/";
          })
          .catch(error => {
            if (error.message) {
              this.signupErrorMessage = error.message;
            } else {
              this.signupErrorMessage =
              "something unexpected went wrong during signup";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    forgotPassword(email) {
      this.isLoading = true;
      $axios
          .post("/user/forgot-password/initiate", {
          // eslint-disable-next-line camelcase
            app_id: this.appId,
            email: email,
            redirect: `${links.web}/login`,
          })
          .then(response => {
            if (response.data.success) {
              new ToastBuilder()
                  .setTitle("Confirm Email")
                  .setText(response.data.reset_link)
                  .setToastType(ToastBuilder.TOAST_TYPE_SUCCESS)
                  .setTimeout(ToastBuilder.TIMEOUT_NONE)
                  .setClosable(true)
                  .build();
            }
            this.forgotPasswordErrorMessage =
            "something unexpectedly went wrong while trying to trigger forgot password";
          })
          .catch(error => {
            if (error.message) {
              this.forgotPasswordErrorMessage = error.message;
            } else {
              this.forgotPasswordErrorMessage =
              "something unexpectedly went wrong while trying to trigger forgot password";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    resetPassword(userId, token, password, confirmPassword) {
      this.isLoading = true;
      $axios
          .post("/user/forgot-password/complete", {
            user_id: userId,
            token: token,
            new_password: password,
            confirm_new_password: confirmPassword,
          })
          .then(response => {
            window.location.href = response.data.redirect;
          })
          .catch(error => {
            this.resetPasswordErrorMessage = error.response.data.message;
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    verifyEmail(userId, emailId, token) {
      this.isLoading = true;
      $axios
          .post(`${this.usersApiV1Url}/user/verify-email/app/${this.appId}`, {
            user_id: userId,
            email_id: emailId,
            token: token,
          })
          .then(response => {
            if (response.data.success) {
              window.location.href = "/";
              return;
            }
            this.verifyEmailErrorMessage = "Something went wrong verifying email";
          })
          .catch(error => {
            if (error.message) {
              this.verifyEmailErrorMessage = error.message;
            } else {
              this.verifyEmailErrorMessage =
              "something unexpectedly went wrong resetting password";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    logoutUser(redirectUri = "/") {
      this.isLoading = true;
      $axios
          .post("/user/logout")
          .then(response => {
            if (response.data.success) {
              window.location.href = redirectUri;
              return;
            }
            this.logoutErrorMessage = "something went wrong during logout";
          })
          .catch(error => {
            if (error.message) {
              this.logoutErrorMessage = error.message;
            } else {
              this.logoutErrorMessage = "something went wrong during logout";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    async verifyUserExists(email) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.verifyUserErrorMessage = "";
      this.signUpNote = "";

      const urlEncodedEmail = encodeURIComponent(email);
      const verifyUserUri = `${this.usersApiV1Url}/user/app/${this.appId}/exists?email=${urlEncodedEmail}`;
      return $axios
          .get(verifyUserUri)
          .then(response => {
            this.userAuthEmail = email;

            const deleted = response.data.deleted === true;
            if (response.data.exists === false || deleted) {
              const { step, signUpNote } = resolveEmailCheckOutcome({ exists: false, deleted, canSignUp: true });
              this.signUpNote = signUpNote;
              this.userFlowSteps.push(step);
              return { email, exists: false, deleted };
            } else if (response.data.invited === true) {
              this.userFlowSteps.push(INVITED_PASSWORD_RESET_FORM);
            } else {
              this.userFlowSteps.push(PASSWORD_FORM);
            }
            this.userFirstName = response.data.first_name;

            return { email, exists: true };
          })
          .catch(error => {
            if (error.message) {
              this.verifyUserErrorMessage = error.message;
            } else {
              this.verifyUserErrorMessage =
              "something went wrong checking to see if user has account";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    fetchEmailStatus() {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios
          .get(`${this.usersApiV1Url}/user/${this.userId}/email/status`)
          .then(response => {
            if (response.data.success) {
              let emailStatus = response.data.email_status;
              if (emailStatus.isEmailConfirmed) {
                emailStatus.requestedNewEmail = this.emailData.email_address;
              }

              this.emailStatus = emailStatus;
            }
            if (!response.data.success) {
              this.userDetailsErrorMessage = response.data.message;
            }
            this.isLoading = false;
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.message;
            } else {
              this.userDetailsErrorMessage = "Check email status failed.";
            }
          });
    },

    cancelEmailChange() {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios
          .post(`${this.usersApiV1Url}/user/${this.userId}/email/cancel-change`)
          .then(response => {
            if (!response.data.success) {
              this.userDetailsErrorMessage = response.data.message;
              this.isLoading = false;
              return;
            }
            this.emailStatus = response.data.emailStatus;
            this.isLoading = false;
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.message;
            } else {
              this.userDetailsErrorMessage =
              "Cancel email change process failed.";
            }
          });
    },

    submitNewEmail(email) {
      if (this.isLoading) {
        return;
      }
      if (this.emailData.email_address === email) {
        this.changeEmailError = "New email cannot be current email";
        return;
      }
      this.isLoading = true;
      $axios
          .post(
            `${this.usersApiV1Url}/user/${this.userId}/email/initiate-change`,
            {
              new_email: email,
              app_id: this.appId,
            },
          )
          .then(response => {
            this.emailStatus = response.data.emailStatus;
            this.userDetailsErrorMessage = response.data.message;
            this.isLoading = false;
          })
          .catch(() => {
            this.changeEmailError = "Unable to change email. Please try again.";
            this.isLoading = false;
          });
    },

    submitNewFirstName(firstName) {
      if (this.loadingChangeFirstName) {
        return;
      }
      this.loadingChangeFirstName = true;
      $axios
          .post(`${this.usersApiV1Url}/user/${this.userId}/first_name/change`, {
            first_name: firstName,
          })
          .then(response => {
            this.loadingChangeFirstName = false;
            const userData = useGeneralStore().userData;
            useGeneralStore().setUserData({
              ...userData,
              first_name: response.data.user_data.first_name,
            });
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.message;
            } else {
              this.userDetailsErrorMessage = "Submit new first name failed";
            }
            this.loadingChangeFirstName = false;
          });
    },

    submitNewLastName(lastName) {
      if (this.loadingChangeLastName) {
        return;
      }
      this.loadingChangeLastName = true;
      $axios
          .post(`${this.usersApiV1Url}/user/${this.userId}/last_name/change`, {
            last_name: lastName,
          })
          .then(response => {
            useGeneralStore().setUserData({
              ...this.userData,
              last_name: response.data.user_data.last_name,
            });
            this.loadingChangeLastName = false;
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.message;
            } else {
              this.userDetailsErrorMessage = "Submit new last name failed";
            }
            this.loadingChangeLastName = false;
          });
    },
    deleteAccount(password) {
      if (this.isDeletingAccount) {
        return Promise.resolve();
      }
      this.isDeletingAccount = true;
      this.deleteAccountErrorMessage = "";
      return $axios
          .post("/user/account/delete", {
            password: password,
          })
          .then(() => {
            window.location.href = useGeneralStore().getRelativeHref("/logout");
          })
          .catch(error => {
            this.deleteAccountErrorMessage =
              error?.response?.data?.message ||
              error?.message ||
              "We could not delete your account. Check your password and try again.";
            this.isDeletingAccount = false;
          });
    },
    submitNewPassword(currentPassword, newPassword) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      return $axios
          .post(`${this.usersApiV1Url}/user/${this.userId}/password/change`, {
            current_password: currentPassword,
            new_password: newPassword,
          })
          .then(() => {
            this.successfulPasswordChange = true;
            this.userDetailsErrorMessage = "";
            this.isLoading = false;
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.response.data.message;
            } else {
              this.userDetailsErrorMessage = "Submit new password failed";
            }
            this.successfulPasswordChange = false;
            this.isLoading = false;
          });
    },
    finalizeCollaboratorOffPlatformInvite(
      orgId,
      userId,
      firstName,
      lastName,
      orgName,
      password,
      token,
    ) {
      this.isLoading = true;
      this.apiPost(
        `/user/${userId}/org/${orgId}/invite/${token}/finalize`,
        {
          first_name: firstName,
          last_name: lastName,
          org_name: orgName,
          password: password,
        },
        () => {
          window.location.href = "/";
        },
        error => {
          this.signupErrorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Something unexpected went wrong during registration";
        },
        () => {
          this.isLoading = false;
        },
      );
    },
    acceptUser(userId, publicationId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios
          .post(`/user/${userId}/publication/${publicationId}/accept`, {})
          .then(response => {
            new SmartFeedEngine().addEntities(response.data.entities);
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.message;
            } else {
              this.userDetailsErrorMessage = "Failed to accept user.";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    rejectUser(userId, publicationId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      $axios
          .post(`/user/${userId}/publication/${publicationId}/reject`, {})
          .then(response => {
            new SmartFeedEngine().addEntities(response.data.entities);
          })
          .catch(error => {
            if (error.message) {
              this.userDetailsErrorMessage = error.message;
            } else {
              this.userDetailsErrorMessage = "Failed to reject user.";
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    deactivateUser(userId, publicationId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/user/${userId}/publication/${publicationId}/deactivate`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          if (error.message) {
            this.userDetailsErrorMessage = error.message;
          } else {
            this.userDetailsErrorMessage = "Failed to deactivate user.";
          }
        },
        () => {
          this.isLoading = false;
        },
      );
    },
    revokeInvitation(userId, publicationId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/user/${userId}/publication/${publicationId}/invitation/revoke`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          if (error.message) {
            this.userDetailsErrorMessage = error.message;
          } else {
            this.userDetailsErrorMessage = "Failed to revoke invitation.";
          }
        },
        () => {
          this.isLoading = false;
        },
      );
    },
    reactivateInvitation(userId, publicationId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/user/${userId}/publication/${publicationId}/invitation/reactivate`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          if (error.message) {
            this.userDetailsErrorMessage = error.message;
          } else {
            this.userDetailsErrorMessage = "Failed to reactivate invitation.";
          }
        },
        () => {
          this.isLoading = false;
        },
      );
    },
    enableUser(userId, publicationId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/user/${userId}/publication/${publicationId}/enable`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          if (error.message) {
            this.userDetailsErrorMessage = error.message;
          } else {
            this.userDetailsErrorMessage = "Failed to enable user.";
          }
        },
        () => {
          this.isLoading = false;
        },
      );
    },
    resendInvitationMail(publicationId, email, firstName, userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/publication/${publicationId}/invitation/resend`,
        {
          email: email,
          first_name: firstName,
          user_id: userId,
        },
        () => {},
        error => {
          if (error.message) {
            this.userDetailsErrorMessage = error.message;
          } else {
            this.userDetailsErrorMessage = "Failed to enable user.";
          }
        },
        () => {
          this.isLoading = false;
        },
      );
    },
    promoteToModerator(userId) {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      this.apiPost(
        `/user/${userId}/promote`,
        {
          user_id: userId,
        },
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message ||
            "Failed to promote user to moderator.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },
    demoteFromModerator(userId) {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      this.apiPost(
        `/user/${userId}/demote`,
        {
          user_id: userId,
        },
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message ||
            "Failed to demote user from moderator.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },
  },
});
